import {
  ReactNode, useRef, useState,
} from 'react';
import {
  Box, Fade, IconButton, useTheme,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Scrollbars } from 'react-custom-scrollbars-4';
import useDebouncedCallback from '@/hooks/useDebouncedCallback';

interface IScrollStatus {
  hasOverflow: boolean;
  isScrolledToBottom: boolean;
  isScrolledToTop: boolean;
}

const getScrollStatus = (inst: Scrollbars): IScrollStatus | null => {
  if (!inst) return null;

  const scrollTop = inst.getScrollTop();
  const scrollHeight = inst.getScrollHeight();
  const clientHeight = inst.getClientHeight();

  if (!clientHeight) return null;

  const hasOverflow = scrollHeight > clientHeight;
  const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
  const isScrolledToTop = scrollTop <= 10; // 10px threshold

  return { hasOverflow, isScrolledToBottom, isScrolledToTop };
};

interface ScrollbarProps {
  children?: ReactNode;
  showOverflowButtons?: boolean;
  regionLabel?: string;
  regionLabelledBy?: string;
}

interface ScrollbarsWithView extends Scrollbars {
  view: HTMLElement
}

const Scrollbar = ({
  children = undefined, showOverflowButtons = false,
  regionLabel = 'Scrollable content',
  regionLabelledBy = 'Scrollable content',
}: ScrollbarProps) => {
  const theme = useTheme();
  const scrollRef = useRef<Scrollbars | null>(null);
  const [showScrollDownButton, setShowScrollDownButton] = useState(false);
  const [showScrollUpButton, setShowScrollUpButton] = useState(false);
  const hasRegionName = regionLabel || regionLabelledBy;

  const smoothScrollTo = (targetTop: number) => {
    const inst = scrollRef.current;
    if (!inst) return;

    const view = (inst as ScrollbarsWithView).view as HTMLElement | undefined;
    if (!view) return;

    // Native smooth scrolling
    if (typeof view.scrollTo === 'function') {
      view.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });
    } else {
    // Fallback if scrollTo isn't supported
      inst.scrollTop(targetTop);
    }
  };

  const handleScroll = useDebouncedCallback(() => {
    if (!showOverflowButtons) return;
    const inst = scrollRef.current;
    if (!inst) return;
    const scrollStatus = getScrollStatus(inst);
    if (!scrollStatus) return;

    const { hasOverflow, isScrolledToBottom, isScrolledToTop } = scrollStatus;
    setShowScrollDownButton(hasOverflow && !isScrolledToBottom);
    setShowScrollUpButton(hasOverflow && !isScrolledToTop);
  }, 500);

  const scrollToBottom = () => {
    const inst = scrollRef.current;
    if (!inst) return;

    // targetTop is the final scroll position (in pixels from the top) needed
    // to scroll all the way to the bottom.
    const targetTop = inst.getScrollHeight() - inst.getClientHeight();
    smoothScrollTo(targetTop);
    setShowScrollDownButton(false);
  };

  const scrollToTop = () => {
    const inst = scrollRef.current;
    if (!inst) return;

    smoothScrollTo(0);
    setShowScrollUpButton(false);
  };

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Scrollbars
        ref={scrollRef}
        autoHide
        onScroll={showOverflowButtons ? handleScroll : undefined}
        onUpdate={showOverflowButtons ? handleScroll : undefined}
        renderThumbVertical={() => (
          <Box
            sx={{
              width: 5,
              background: `${theme.colors.alpha.black[10]}`,
              borderRadius: `${theme.general.borderRadiusLg}`,
              transition: `${theme.transitions.create(['background'])}`,
              '&:hover': { background: `${theme.colors.alpha.black[30]}` },
            }}
          />
        )}
        renderView={(props) => (
          <div
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
             // Only expose as a landmark if it has a unique name
            role={hasRegionName ? 'region' : undefined}
            aria-label={regionLabel}
            aria-labelledby={regionLabelledBy}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
          />
        )}
      >
        {children}
      </Scrollbars>
      {showOverflowButtons && (
        <>
          <Fade in={showScrollDownButton}>
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 2,
                zIndex: 10,
              }}
            >
              <IconButton
                sx={{
                  padding: '4px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(31, 76, 178, 0.4)',
                  '&:hover': { backgroundColor: 'rgba(31, 76, 178, 0.8)' },
                }}
                onClick={scrollToBottom}
                aria-label="Scroll to bottom"
              >
                <ArrowDownwardIcon
                  sx={{
                    fontSize: '20px',
                    color: 'white',
                    animation: 'wink 1s ease-in-out infinite',
                    '@keyframes wink': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                      '100%': { opacity: 1 },
                    },
                  }}
                />
              </IconButton>
            </Box>
          </Fade>
          <Fade in={showScrollUpButton}>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 2,
                zIndex: 10,
              }}
            >
              <IconButton
                sx={{
                  padding: '4px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(31, 76, 178, 0.4)',
                  '&:hover': { backgroundColor: 'rgba(31, 76, 178, 0.8)' },
                }}
                onClick={scrollToTop}
                aria-label="Scroll to top"
              >
                <ArrowUpwardIcon
                  sx={{
                    fontSize: '20px',
                    color: 'white',
                    animation: 'wink 1s ease-in-out infinite',
                    '@keyframes wink': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                      '100%': { opacity: 1 },
                    },
                  }}
                />
              </IconButton>
            </Box>
          </Fade>
        </>
      )}
    </Box>
  );
};

export default Scrollbar;
